import React, { useState, useRef, useEffect } from 'react';
import {
  Button, Card, CardBody, FormGroup, Label, Input, Badge,
} from 'reactstrap';
import { FiMessageCircle, FiX, FiPlus, FiList, FiHelpCircle, FiPhone, FiChevronRight, FiPaperclip } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useBranding } from '../../contexts/BrandingContext';

const CATEGORIES = ['Billing', 'Technical', 'Booking', 'Inventory', 'Dispute', 'Other'];
const PRIORITIES = ['Low', 'Medium', 'High'];
const PRIORITY_COLORS = { Low: 'secondary', Medium: 'warning', High: 'danger' };
const STATUS_COLORS = { Open: 'warning', 'In Progress': 'primary', Resolved: 'success', Closed: 'secondary' };

const RECENT_TICKETS = [
  { id: 'TKT-1042', subject: 'Invoice mismatch for Booking #BK-209', status: 'In Progress', created: '2 hours ago' },
  { id: 'TKT-1038', subject: 'GRN rejected incorrectly', status: 'Open', created: '1 day ago' },
  { id: 'TKT-1035', subject: 'Unable to dispatch from Zone B', status: 'Resolved', created: '3 days ago' },
];

const SupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('menu'); // 'menu' | 'create' | 'tickets' | 'faq' | 'contact'
  const branding = useBranding();
  const panelRef = useRef(null);

  const [form, setForm] = useState({
    category: 'Technical', subject: '', description: '', priority: 'Medium', attachment: null,
  });

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest('.support-widget-trigger')) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const handleSubmitTicket = (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error('Please fill subject and description');
      return;
    }
    const ticketId = `TKT-${Math.floor(1000 + Math.random() * 9000)}`;
    toast.success(`Ticket ${ticketId} created`);
    setForm({ category: 'Technical', subject: '', description: '', priority: 'Medium', attachment: null });
    setView('menu');
  };

  const toggleWidget = () => {
    setIsOpen(!isOpen);
    if (!isOpen) setView('menu');
  };

  return (
    <>
      {/* Floating Button */}
      <button
        className="support-widget-trigger"
        onClick={toggleWidget}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          width: 56, height: 56, borderRadius: '50%', border: 'none',
          backgroundColor: '#3f51b5', color: '#fff', cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(63,81,181,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: !isOpen ? 'supportPulse 2s infinite' : 'none',
          transition: 'transform 0.2s',
          transform: isOpen ? 'rotate(0deg)' : 'rotate(0deg)',
        }}
        title="Support"
      >
        {isOpen ? <FiX size={24} /> : <FiMessageCircle size={24} />}
      </button>

      {/* Slide-up Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          style={{
            position: 'fixed', bottom: 90, right: 24, zIndex: 9998,
            width: 360, maxHeight: 520, borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            background: '#fff', overflow: 'hidden',
            animation: 'supportSlideUp 0.25s ease-out',
          }}
        >
          {/* Header */}
          <div style={{ background: '#3f51b5', color: '#fff', padding: '16px 20px' }}>
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 text-white fw-bold">
                {view === 'menu' && 'Support'}
                {view === 'create' && 'Create Ticket'}
                {view === 'tickets' && 'My Tickets'}
                {view === 'faq' && 'FAQ'}
                {view === 'contact' && 'Contact Us'}
              </h6>
              {view !== 'menu' && (
                <button
                  onClick={() => setView('menu')}
                  style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  Back
                </button>
              )}
            </div>
          </div>

          <div style={{ maxHeight: 440, overflowY: 'auto', padding: '16px 20px' }}>
            {/* Main Menu */}
            {view === 'menu' && (
              <>
                <div className="d-flex flex-column gap-2 mb-3">
                  {[
                    { icon: <FiHelpCircle />, label: 'Help Center', action: () => window.location.href = '/help-center' },
                    { icon: <FiPlus />, label: 'Create Support Ticket', action: () => setView('create') },
                    { icon: <FiList />, label: 'View My Tickets', action: () => setView('tickets') },
                    { icon: <FiHelpCircle />, label: 'FAQ', action: () => setView('faq') },
                    { icon: <FiPhone />, label: 'Contact Us', action: () => setView('contact') },
                  ].map((item, i) => (
                    <button
                      key={i}
                      onClick={item.action}
                      className="d-flex align-items-center justify-content-between w-100 text-left"
                      style={{
                        padding: '12px 16px', background: '#f8f9fa', border: '1px solid #e9ecef',
                        borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem',
                      }}
                    >
                      <span className="d-flex align-items-center">
                        <span className="mr-2" style={{ color: '#3f51b5' }}>{item.icon}</span>
                        {item.label}
                      </span>
                      <FiChevronRight size={16} className="text-muted" />
                    </button>
                  ))}
                </div>

                <h6 className="fw-bold small text-muted mb-2">Recent Tickets</h6>
                {RECENT_TICKETS.map((t) => (
                  <div key={t.id} className="d-flex justify-content-between align-items-start mb-2 p-2 rounded" style={{ background: '#f8f9fa', fontSize: '0.85rem' }}>
                    <div>
                      <div className="fw-bold">{t.id}</div>
                      <div className="text-muted small">{t.subject}</div>
                    </div>
                    <Badge color={STATUS_COLORS[t.status]} className="ml-2" style={{ fontSize: '0.7rem' }}>{t.status}</Badge>
                  </div>
                ))}

                <div className="text-center mt-3">
                  <Link to="/settings/support-tickets" className="small text-primary" onClick={() => setIsOpen(false)}>
                    View all tickets
                  </Link>
                </div>
              </>
            )}

            {/* Create Ticket */}
            {view === 'create' && (
              <form onSubmit={handleSubmitTicket}>
                <FormGroup>
                  <Label className="small fw-bold">Category</Label>
                  <Input type="select" bsSize="sm" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label className="small fw-bold">Subject</Label>
                  <Input type="text" bsSize="sm" placeholder="Brief summary..." value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label className="small fw-bold">Description</Label>
                  <Input type="textarea" rows={3} bsSize="sm" placeholder="Describe your issue..." value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                </FormGroup>
                <FormGroup>
                  <Label className="small fw-bold">Priority</Label>
                  <Input type="select" bsSize="sm" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label className="small fw-bold">Attachment</Label>
                  <Input type="file" bsSize="sm" onChange={e => setForm({ ...form, attachment: e.target.files[0] })} />
                </FormGroup>
                <Button color="primary" size="sm" block type="submit">Submit Ticket</Button>
              </form>
            )}

            {/* My Tickets */}
            {view === 'tickets' && (
              <>
                {RECENT_TICKETS.map((t) => (
                  <div key={t.id} className="border rounded p-3 mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-bold">{t.id}</span>
                      <Badge color={STATUS_COLORS[t.status]}>{t.status}</Badge>
                    </div>
                    <div className="small">{t.subject}</div>
                    <div className="small text-muted mt-1">{t.created}</div>
                  </div>
                ))}
                <div className="text-center mt-2">
                  <Link to="/settings/support-tickets" className="small text-primary" onClick={() => setIsOpen(false)}>
                    View all tickets
                  </Link>
                </div>
              </>
            )}

            {/* FAQ */}
            {view === 'faq' && (
              <>
                {[
                  { q: 'How do I create a GRN?', a: 'Navigate to Inventory > GRN (Inbound) and click "Create GRN".' },
                  { q: 'How do I raise a dispute?', a: 'Go to Disputes and click "New Dispute" with relevant booking details.' },
                  { q: 'How do I track my shipment?', a: 'Visit Logistics > Shipments and enter your shipment ID.' },
                  { q: 'How to manage rate cards?', a: 'Go to Billing > Rate Cards to create and manage pricing templates.' },
                  { q: 'How to export invoices?', a: 'Open the invoice detail page and click the PDF/Excel export button.' },
                ].map((faq, i) => (
                  <div key={i} className="border-bottom pb-2 mb-2">
                    <div className="fw-bold small">{faq.q}</div>
                    <div className="small text-muted">{faq.a}</div>
                  </div>
                ))}
              </>
            )}

            {/* Contact Us */}
            {view === 'contact' && (
              <div>
                <div className="text-center mb-3">
                  <div className="rounded-circle bg-soft-primary d-inline-flex align-items-center justify-content-center mb-2" style={{ width: 60, height: 60 }}>
                    <FiPhone size={24} className="text-primary" />
                  </div>
                </div>
                <div className="border rounded p-3 mb-2">
                  <div className="small fw-bold">Email Support</div>
                  <div className="small text-muted">{branding?.support_email || 'support@warehousewala.in'}</div>
                </div>
                <div className="border rounded p-3 mb-2">
                  <div className="small fw-bold">Phone Support</div>
                  <div className="small text-muted">{branding?.support_phone || '+91 1800-200-3456'}</div>
                </div>
                <div className="border rounded p-3 mb-2">
                  <div className="small fw-bold">Working Hours</div>
                  <div className="small text-muted">Mon-Sat, 9:00 AM - 6:00 PM IST</div>
                </div>
                <div className="border rounded p-3">
                  <div className="small fw-bold">Address</div>
                  <div className="small text-muted">{branding?.company_address || 'Warehouse Management HQ, Lower Parel, Mumbai 400013'}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes supportPulse {
          0% { box-shadow: 0 4px 12px rgba(63,81,181,0.4); }
          50% { box-shadow: 0 4px 24px rgba(63,81,181,0.6); }
          100% { box-shadow: 0 4px 12px rgba(63,81,181,0.4); }
        }
        @keyframes supportSlideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default SupportWidget;
